import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Loader2, Lock, ArrowLeft, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminImpact = () => {
  const [passcode, setPasscode] = useState(sessionStorage.getItem("ohvse_admin") || "");
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState({
    dollars_raised: 0,
    kids_reached: 0,
    programs_run: 0,
    free_percent: 100,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/impact`)
      .then((r) => {
        setStats(r.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const login = (e) => {
    e.preventDefault();
    if (!passcode) return;
    sessionStorage.setItem("ohvse_admin", passcode);
    setAuthed(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/admin/impact`,
        {
          dollars_raised: Number(stats.dollars_raised),
          kids_reached: Number(stats.kids_reached),
          programs_run: Number(stats.programs_run),
          free_percent: Number(stats.free_percent),
        },
        { headers: { "X-Admin-Passcode": passcode } }
      );
      setStats(res.data);
      toast.success("Impact counters updated on the live site.");
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid admin passcode.");
        sessionStorage.removeItem("ohvse_admin");
        setAuthed(false);
      } else {
        toast.error(err?.response?.data?.detail || "Could not save.");
      }
    } finally {
      setSaving(false);
    }
  };

  const onChange = (k) => (e) => setStats({ ...stats, [k]: e.target.value });

  if (!authed) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-[#111111] border border-white/10 p-10 space-y-6"
        >
          <div className="flex items-center gap-3">
            <Lock size={20} className="text-[#C4F542]" />
            <h1 className="font-display text-3xl text-white">ADMIN · IMPACT</h1>
          </div>
          <p className="text-sm text-zinc-400 font-body">
            Enter your admin passcode to update the live impact counters shown on the site.
          </p>
          <input
            type="password"
            data-testid="admin-passcode"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="Admin passcode"
            className="w-full bg-[#1A1A1A] border border-white/15 text-white px-4 py-3 focus:outline-none focus:border-[#C4F542] font-body"
            autoFocus
          />
          <button
            type="submit"
            data-testid="admin-login-btn"
            className="w-full bg-[#C4F542] text-black font-bold uppercase tracking-widest text-sm py-4 hover:bg-white transition-colors"
          >
            Unlock
          </button>
          <Link to="/" className="block text-xs uppercase tracking-widest text-zinc-500 hover:text-[#C4F542] text-center">
            <ArrowLeft size={12} className="inline mr-1" /> Back to site
          </Link>
        </form>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="text-[#C4F542] animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black p-6 md:p-12">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[#C4F542] mb-2">Admin</p>
            <h1 className="font-display text-4xl md:text-5xl text-white">IMPACT COUNTERS</h1>
          </div>
          <Link to="/" className="text-xs uppercase tracking-widest text-zinc-400 hover:text-[#C4F542]">
            <ArrowLeft size={12} className="inline mr-1" /> Back
          </Link>
        </div>

        <form
          onSubmit={save}
          data-testid="admin-impact-form"
          className="bg-[#111111] border border-white/10 p-8 md:p-10 space-y-5"
        >
          <Field
            label="Dollars Raised (USD)"
            testId="admin-dollars"
            type="number"
            step="0.01"
            min="0"
            value={stats.dollars_raised}
            onChange={onChange("dollars_raised")}
          />
          <Field
            label="Kids Reached"
            testId="admin-kids"
            type="number"
            min="0"
            value={stats.kids_reached}
            onChange={onChange("kids_reached")}
          />
          <Field
            label="Programs Run"
            testId="admin-programs"
            type="number"
            min="0"
            value={stats.programs_run}
            onChange={onChange("programs_run")}
          />
          <Field
            label="Free for Families (%)"
            testId="admin-free"
            type="number"
            min="0"
            max="100"
            value={stats.free_percent}
            onChange={onChange("free_percent")}
          />

          <button
            type="submit"
            data-testid="admin-save-btn"
            disabled={saving}
            className="w-full inline-flex items-center justify-center gap-3 bg-[#C4F542] text-black font-bold uppercase tracking-widest text-sm py-4 hover:bg-white transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? "Saving..." : "Save & Publish"}
          </button>
        </form>

        <p className="text-xs text-zinc-500 font-body mt-6">
          Changes go live immediately on the homepage. The counter refreshes every 30 seconds for visitors.
        </p>
      </div>
    </div>
  );
};

const Field = ({ label, testId, ...rest }) => (
  <div className="flex flex-col gap-2">
    <label className="text-xs uppercase tracking-[0.25em] text-zinc-400">{label}</label>
    <input
      data-testid={testId}
      {...rest}
      className="bg-[#1A1A1A] border border-white/15 text-white px-4 py-3 focus:outline-none focus:border-[#C4F542] font-body"
    />
  </div>
);
